import i18next from "i18next";

type Input = {
  label: string;
  score: number;
  param1?: string;
};

type MessageResult = {
  isSuccess: boolean;
  message: string;
  label: string;
  score: number;
  threshold: number;
};

export function generateMessage({ label, score, param1 }: Input): MessageResult {
  const t = i18next.t.bind(i18next);

  const messages: Record<string, { warning: string; success: string }> = {
    "total_title_score": {
      warning: t('productStates.title_warning'),
      success: t('productStates.title_success'),
    },
    "total_desc_html_score": {
      warning: t('productStates.description_warning'),
      success: t('productStates.description_success'),
    },
    "images_count": {
      warning: score === 0 ? t('productStates.image_warning_none') : 
        score === 1 ? t('productStates.image_warning_one') :
        t('productStates.image_warning_multiple', { count: score }),
      success: t('productStates.image_success'),
    },
    "total_seo_description_score": {
      warning: t('productStates.seo_warning'),
      success: t('productStates.seo_success'),
    },
    "total_category_placement_score": {
      warning: t('productStates.category_warning'),
      success: t('productStates.category_success'),
    },
    "total_alt_text_score": {
      warning: t('productStates.alt_text_warning'),
      success: t('productStates.alt_text_success'),
    },
    "total_tags_score": {
      warning: t('productStates.tags_warning'),
      success: t('productStates.tags_success'),
    }
  };

  const thresholds: Record<string, number> = {
    "total_title_score": 80,
    "total_desc_html_score": 80,
    "images_count": 5,
    "total_seo_description_score": 80,
    "total_category_placement_score": 80,
    "total_alt_text_score": 80,
    "total_tags_score": 10
  };

  const labelMessages = messages[label];
  if (!labelMessages) {
    throw new Error(`Label "${label}" not supported`);
  }

  const threshold = thresholds[label];
  if (threshold === undefined) {
    throw new Error(`Threshold for "${label}" not defined`);
  }

  const isSuccess = score >= threshold;

  return {
    isSuccess,
    message: isSuccess ? labelMessages.success : labelMessages.warning,
    label,
    score,
    threshold
  }
}

export function generateAllMessages(inputs: Input[]): MessageResult[] {
  const results = inputs.map(input => generateMessage(input));
  
  // Sort results: failures first, then successes
  return results.sort((a, b) => {
    // First, sort by success/failure
    if (a.isSuccess !== b.isSuccess) {
      return a.isSuccess ? 1 : -1;
    }
    // Then sort by how far they are from their threshold (for failures)
    if (!a.isSuccess) {
      const aDistance = a.threshold - a.score;
      const bDistance = b.threshold - b.score;
      return bDistance - aDistance; // Larger distance (worse score) comes first
    }
    // For successes, sort by how much they exceed the threshold
    const aExceed = a.score - a.threshold;
    const bExceed = b.score - b.threshold;
    return bExceed - aExceed; // Higher exceeding score comes first
  });
}